"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ExternalLink, Github, Filter } from "lucide-react";
import { projects } from "../data/mock";
import Layout from "../components/layout/Layout";

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState("All");

  const categories = ["All", ...new Set(projects.map((p) => p.category))];

  const filteredProjects =
    activeFilter === "All"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <Layout>
      {/* SEO */}
      <Helmet>
        <title>Projects | Gauransh Jaroli</title>
        <meta
          name="description"
          content="Real client projects and production web applications."
        />
      </Helmet>

      {/* HERO */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-950" />

        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-emerald-400/10 blur-3xl rounded-full" />
        <div className="absolute -top-32 right-0 w-[420px] h-[420px] bg-blue-500/10 blur-3xl rounded-full" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-10 max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              My{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent">
                Projects
              </span>
            </h1>

            <p className="text-lg text-slate-300">
              Real deployed client work and production-ready systems.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FILTER */}
      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter size={18} className="text-slate-400" />

            {categories.map((category) => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter(category)}
                className={`px-4 py-2 rounded-full text-sm transition ${
                  activeFilter === category
                    ? "bg-gradient-to-r from-emerald-400 to-blue-500 text-black"
                    : "bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-28">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, index) => (
                <motion.a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={project.id}
                  layout
                  variants={fadeInUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ delay: index * 0.05 }}
                  className="group relative block rounded-2xl cursor-pointer"
                >
                  {/* Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-blue-500/20 blur-xl rounded-2xl" />
                  </div>

                  {/* Card */}
                  <motion.div
                    whileHover={{
                      scale: 1.04,
                      y: -6,
                    }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="relative h-full backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8"
                  >
                    {/* Accent */}
                    <div className="h-[2px] w-full bg-gradient-to-r from-emerald-400 to-blue-500 mb-6" />

                    {/* Header */}
                    <div className="flex justify-between mb-4">
                      <span className="text-xs px-3 py-1 rounded-full bg-emerald-400/10 text-emerald-400">
                        {project.category}
                      </span>

                      {project.featured && (
                        <span className="text-xs bg-blue-400/10 text-blue-400 px-2 py-1 rounded-full">
                          Featured
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-emerald-400 transition">
                      {project.title}
                    </h3>

                    {/* SHORT DESCRIPTION */}
                    <p
                      className="text-slate-300 text-sm mb-3 overflow-hidden"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {project.description}
                    </p>

                    <p
                      className="text-slate-400 text-xs leading-relaxed overflow-hidden"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {project.longDescription}
                    </p>

                    {/* Tech */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.techStack.map((tech) => (
                        <span
                          key={tech}
                          className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded text-slate-200"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Bottom */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="text-sm text-emerald-400 flex items-center gap-1">
                        <ExternalLink size={16} />
                        View Project
                      </span>

                      {project.github && (
                        <span className="text-sm text-blue-400 flex items-center gap-1">
                          <Github size={16} />
                        </span>
                      )}
                    </div>
                  </motion.div>
                </motion.a>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Empty */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              No projects found.
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Portfolio;
